import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Receipt, LocalOffer, AddCircleOutline, InfoOutlined } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { financeAPI } from '../../services/api';
import { getCountries } from '../../data/locationData';

const subscriptionModels = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'six_months', label: '6 Months' },
  { value: 'yearly', label: 'Yearly' },
];

const batchTypes = [
  { value: 'one_on_one', label: '1:1' },
  { value: 'group', label: 'Group' },
];

// Country → currency (ISO 4217) used elsewhere; currency dropdown shows these
const countryToCurrency = {
  USA: 'USD',
  India: 'INR',
  Canada: 'CAD',
  UK: 'GBP',
  Australia: 'AUD',
  'New Zealand': 'NZD',
  Singapore: 'SGD',
  Japan: 'JPY',
  UAE: 'AED',
  Germany: 'EUR',
  Indonesia: 'IDR',
  Kuwait: 'KWD',
  Netherlands: 'EUR',
  Oman: 'OMR',
  'Saudi Arabia': 'SAR',
  Sweden: 'SEK',
};

const currencyList = [...new Set(Object.values(countryToCurrency))].sort();
const countryList = getCountries();

const FinancePricing = () => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [addRowModalOpen, setAddRowModalOpen] = useState(false);
  const [editRowModalOpen, setEditRowModalOpen] = useState(false);
  const [editRowId, setEditRowId] = useState(null);

  const [filters, setFilters] = useState({
    country: '',
    currency: '',
    batchType: '',
    subscriptionModel: '',
    classesPerWeek: '',
    page: 1,
    limit: 20,
  });

  const [newRow, setNewRow] = useState({
    country: '',
    currency: '',
    batchType: '',
    subscriptionModel: '',
    classesPerWeek: 1,
    pricePerSession: '',
    effectiveFrom: '',
  });

  const [editRow, setEditRow] = useState({
    country: '',
    currency: '',
    batchType: '',
    subscriptionModel: '',
    classesPerWeek: 1,
    pricePerSession: '',
    effectiveFrom: '',
  });

  const [addOnPricingFilters, setAddOnPricingFilters] = useState({
    country: '',
    currency: '',
    page: 1,
    limit: 20,
  });
  const [newAddOnRow, setNewAddOnRow] = useState({
    country: '',
    currency: '',
    addOnType: '',
    unitPrice: '',
  });
  const [editAddOnId, setEditAddOnId] = useState(null);
  const [editAddOnUnitPrice, setEditAddOnUnitPrice] = useState('');

  const { data, isLoading } = useQuery(
    ['financePricing', filters],
    async () => {
      const params = {
        country: filters.country || undefined,
        currency: filters.currency || undefined,
        batchType: filters.batchType || undefined,
        subscriptionModel: filters.subscriptionModel || undefined,
        classesPerWeek: filters.classesPerWeek ? Number(filters.classesPerWeek) : undefined,
        page: filters.page,
        limit: filters.limit,
      };
      const res = await financeAPI.listPricing(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

  const createMutation = useMutation(
    async (payload) => {
      const res = await financeAPI.createPricing(payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Pricing row created');
        queryClient.invalidateQueries('financePricing');
        setAddRowModalOpen(false);
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to create pricing row');
        toast.error(msg);
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, payload }) => {
      const res = await financeAPI.updatePricing(id, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Pricing row updated');
        queryClient.invalidateQueries('financePricing');
        setEditRowModalOpen(false);
        setEditRowId(null);
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to update pricing row');
        toast.error(msg);
      },
    }
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? value : 1,
    }));
  };

  const handleNewRowChange = (field, value) => {
    setNewRow((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'country' && value && countryToCurrency[value]) {
        next.currency = countryToCurrency[value];
      }
      return next;
    });
  };

  const handleCreate = () => {
    if (
      !newRow.country ||
      !newRow.currency ||
      !newRow.batchType ||
      !newRow.subscriptionModel ||
      !newRow.classesPerWeek ||
      !newRow.pricePerSession ||
      !newRow.effectiveFrom
    ) {
      toast.error('Please fill all fields for new pricing row');
      return;
    }
    createMutation.mutate({
      country: newRow.country.trim(),
      currency: newRow.currency.trim().toUpperCase(),
      batchType: newRow.batchType,
      subscriptionModel: newRow.subscriptionModel,
      classesPerWeek: Number(newRow.classesPerWeek),
      pricePerSession: Number(newRow.pricePerSession),
      effectiveFrom: newRow.effectiveFrom,
    });
  };

  const handleOpenEditRow = (row) => {
    setEditRowId(row.id);
    setEditRow({
      country: row.country || '',
      currency: row.currency || '',
      batchType: row.batch_type || '',
      subscriptionModel: row.subscription_model || '',
      classesPerWeek: row.classes_per_week || 1,
      pricePerSession: row.price_per_session || '',
      effectiveFrom: row.effective_from || '',
    });
    setEditRowModalOpen(true);
  };

  const handleEditRowChange = (field, value) => {
    setEditRow((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'country' && value && countryToCurrency[value]) {
        next.currency = countryToCurrency[value];
      }
      return next;
    });
  };

  const handleUpdate = () => {
    if (
      !editRowId ||
      !editRow.country ||
      !editRow.currency ||
      !editRow.batchType ||
      !editRow.subscriptionModel ||
      !editRow.classesPerWeek ||
      !editRow.pricePerSession ||
      !editRow.effectiveFrom
    ) {
      toast.error('Please fill all fields for pricing row');
      return;
    }
    updateMutation.mutate({
      id: editRowId,
      payload: {
        country: editRow.country.trim(),
        currency: editRow.currency.trim().toUpperCase(),
        batchType: editRow.batchType,
        subscriptionModel: editRow.subscriptionModel,
        classesPerWeek: Number(editRow.classesPerWeek),
        pricePerSession: Number(editRow.pricePerSession),
        effectiveFrom: editRow.effectiveFrom,
      },
    });
  };

  const rows = data?.data || [];
  const pagination = data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 };

  const discountCatalogQuery = useQuery(
    ['financeDiscountCatalog'],
    async () => {
      const res = await financeAPI.getDiscountCatalog();
      return res.data;
    }
  );

  const addOnCatalogQuery = useQuery(
    ['financeAddOnCatalog'],
    async () => {
      const res = await financeAPI.getAddOnCatalog();
      return res.data;
    }
  );

  const addOnPricingQuery = useQuery(
    ['financeAddOnPricing', addOnPricingFilters],
    async () => {
      const params = {
        country: addOnPricingFilters.country || undefined,
        currency: addOnPricingFilters.currency || undefined,
        page: addOnPricingFilters.page,
        limit: addOnPricingFilters.limit,
      };
      const res = await financeAPI.listAddOnPricing(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

  const discountCatalog = discountCatalogQuery.data?.data || [];
  const addOnCatalog = addOnCatalogQuery.data?.data || [];
  const addOnPricingRows = addOnPricingQuery.data?.data || [];
  const addOnPricingPagination = addOnPricingQuery.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 };

  const [discountEdits, setDiscountEdits] = useState({});

  const updateDiscountCatalogMutation = useMutation(
    async ({ type, payload }) => {
      const res = await financeAPI.updateDiscountCatalogType(type, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Discount type updated');
        queryClient.invalidateQueries('financeDiscountCatalog');
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to update discount type');
        toast.error(msg);
      },
    }
  );

  const getEdit = (row) => {
    const e = discountEdits[row.type];
    return {
      label: e?.label ?? row.label ?? '',
      defaultIsPercentage:
        e?.defaultIsPercentage ?? !!row.defaultIsPercentage,
      defaultValue: e?.defaultValue ?? (row.defaultValue ?? null),
      isValueLocked: e?.isValueLocked ?? !!row.isValueLocked,
      isActive: e?.isActive ?? !!row.isActive,
    };
  };

  const setEditField = (type, field, value) => {
    setDiscountEdits((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveDiscountType = (type) => {
    const row = discountCatalog.find((d) => d.type === type);
    if (!row) return;
    const e = getEdit(row);
    updateDiscountCatalogMutation.mutate({
      type,
      payload: {
        label: e.label,
        defaultIsPercentage: !!e.defaultIsPercentage,
        defaultValue: e.defaultValue === '' ? null : e.defaultValue,
        isValueLocked: !!e.isValueLocked,
        isActive: !!e.isActive,
      },
    });
  };

  const createAddOnPricingMutation = useMutation(
    async (payload) => {
      const res = await financeAPI.createAddOnPricing(payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Add-on pricing added');
        queryClient.invalidateQueries('financeAddOnPricing');
        setNewAddOnRow({ country: '', currency: '', addOnType: '', unitPrice: '' });
      },
      onError: (err) => {
        const msg = err.response?.data?.message || (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join(', ') : 'Failed to add');
        toast.error(msg);
      },
    }
  );

  const updateAddOnPricingMutation = useMutation(
    async ({ id, unitPrice }) => {
      const res = await financeAPI.updateAddOnPricing(id, { unitPrice });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Add-on pricing updated');
        queryClient.invalidateQueries('financeAddOnPricing');
        setEditAddOnId(null);
      },
      onError: (err) => {
        const msg = err.response?.data?.message || 'Failed to update';
        toast.error(msg);
      },
    }
  );

  const deleteAddOnPricingMutation = useMutation(
    async (id) => {
      await financeAPI.deleteAddOnPricing(id);
    },
    {
      onSuccess: () => {
        toast.success('Add-on pricing removed');
        queryClient.invalidateQueries('financeAddOnPricing');
      },
      onError: (err) => {
        const msg = err.response?.data?.message || 'Failed to remove';
        toast.error(msg);
      },
    }
  );

  const handleAddOnPricingFilterChange = (field, value) => {
    setAddOnPricingFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? value : 1,
    }));
  };

  const handleCreateAddOnPricing = () => {
    const { country, currency, addOnType, unitPrice } = newAddOnRow;
    if (!country || !currency || !addOnType || unitPrice === '' || Number(unitPrice) < 0) {
      toast.error('Country, currency, add-on type, and unit price are required');
      return;
    }
    createAddOnPricingMutation.mutate({
      country,
      currency,
      addOnType,
      unitPrice: Number(unitPrice),
    });
  };

  const handleOpenEditAddOn = (row) => {
    setEditAddOnId(row.id);
    setEditAddOnUnitPrice(String(row.unitPrice ?? ''));
  };

  const handleSaveEditAddOn = () => {
    if (!editAddOnId || editAddOnUnitPrice === '' || Number(editAddOnUnitPrice) < 0) {
      toast.error('Enter a valid unit price');
      return;
    }
    updateAddOnPricingMutation.mutate({
      id: editAddOnId,
      unitPrice: Number(editAddOnUnitPrice),
    });
  };

  const addOnTypeToLabel = (key) => addOnCatalog.find((a) => a.key === key)?.label || key;

  const cardHeaderSx = { display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" color="text.primary" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
            Fee Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage enrollment details and billing for individual students.
          </Typography>
        </Box>
        {tabValue === 0 && (
          <Button variant="contained" onClick={() => setAddRowModalOpen(true)} sx={{ borderRadius: 2 }}>
            Add Row
          </Button>
        )}
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }}>
        <Tab label="Pricing Matrix" id="finance-tab-0" icon={<Receipt sx={{ fontSize: 20 }} />} iconPosition="start" />
        <Tab label="Discounts" id="finance-tab-1" icon={<LocalOffer sx={{ fontSize: 20 }} />} iconPosition="start" />
        <Tab label="Add-ons" id="finance-tab-2" icon={<AddCircleOutline sx={{ fontSize: 20 }} />} iconPosition="start" />
      </Tabs>

      {tabValue === 0 && (
        <>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <Box sx={cardHeaderSx}>
          <Receipt sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="700">Pricing Matrix</Typography>
            <Typography variant="body2" color="text.secondary">Set price per session by country, plan, and batch type.</Typography>
          </Box>
        </Box>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Country"
              value={filters.country}
              onChange={(e) => handleFilterChange('country', e.target.value)}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2, bgcolor: 'grey.50' } }}
            >
              <MenuItem value="">All</MenuItem>
              {countryList.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="Currency"
              value={filters.currency}
              onChange={(e) => handleFilterChange('currency', e.target.value)}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2, bgcolor: 'grey.50' } }}
            >
              <MenuItem value="">All</MenuItem>
              {currencyList.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="Batch Type"
              value={filters.batchType}
              onChange={(e) => handleFilterChange('batchType', e.target.value)}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2, bgcolor: 'grey.50' } }}
            >
              <MenuItem value="">All</MenuItem>
              {batchTypes.map((bt) => (
                <MenuItem key={bt.value} value={bt.value}>
                  {bt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Plan"
              value={filters.subscriptionModel}
              onChange={(e) => handleFilterChange('subscriptionModel', e.target.value)}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2, bgcolor: 'grey.50' } }}
            >
              <MenuItem value="">All</MenuItem>
              {subscriptionModels.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Classes/Week"
              type="number"
              value={filters.classesPerWeek}
              onChange={(e) => handleFilterChange('classesPerWeek', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 1, max: 7, step: 1 }}
              InputProps={{ sx: { borderRadius: 2, bgcolor: 'grey.50' } }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" onClick={() => setAddRowModalOpen(true)}>
              Add Row
            </Button>
          </Grid>
        </Grid>
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small" sx={{ '& .MuiTableRow-root td': { py: 1 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Country</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Batch Type</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Classes/Week</TableCell>
                <TableCell align="right">Price/Session</TableCell>
                <TableCell>Effective From</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={8}>Loading...</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8}>No pricing rows. Click Add Row to create one.</TableCell></TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.country}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell>{row.batch_type}</TableCell>
                      <TableCell>{row.subscription_model}</TableCell>
                      <TableCell align="right">{row.classes_per_week}</TableCell>
                      <TableCell align="right">{row.price_per_session}</TableCell>
                      <TableCell>{row.effective_from}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleOpenEditRow(row)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={pagination.total || 0}
              page={(filters.page || 1) - 1}
              onPageChange={(_, newPage) => handleFilterChange('page', newPage + 1)}
              rowsPerPage={filters.limit}
              onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10) || 20)}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </TableContainer>
      </Paper>

      <Dialog open={addRowModalOpen} onClose={() => setAddRowModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add pricing row</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Country" value={newRow.country} onChange={(e) => handleNewRowChange('country', e.target.value)} fullWidth size="small">
                <MenuItem value="">Select</MenuItem>
                {countryList.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Currency" value={newRow.currency} onChange={(e) => handleNewRowChange('currency', e.target.value)} fullWidth size="small">
                <MenuItem value="">Select</MenuItem>
                {currencyList.map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Batch Type" value={newRow.batchType} onChange={(e) => handleNewRowChange('batchType', e.target.value)} fullWidth size="small">
                {batchTypes.map((bt) => <MenuItem key={bt.value} value={bt.value}>{bt.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Plan" value={newRow.subscriptionModel} onChange={(e) => handleNewRowChange('subscriptionModel', e.target.value)} fullWidth size="small">
                {subscriptionModels.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Classes/Week"
                type="number"
                value={newRow.classesPerWeek}
                onChange={(e) => handleNewRowChange('classesPerWeek', e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 7, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Price/Session" type="number" value={newRow.pricePerSession} onChange={(e) => handleNewRowChange('pricePerSession', e.target.value)} fullWidth size="small" inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Effective From" type="date" value={newRow.effectiveFrom} onChange={(e) => handleNewRowChange('effectiveFrom', e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRowModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={createMutation.isLoading}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editRowModalOpen} onClose={() => setEditRowModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit pricing row</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Country" value={editRow.country} onChange={(e) => handleEditRowChange('country', e.target.value)} fullWidth size="small">
                <MenuItem value="">Select</MenuItem>
                {countryList.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Currency" value={editRow.currency} onChange={(e) => handleEditRowChange('currency', e.target.value)} fullWidth size="small">
                <MenuItem value="">Select</MenuItem>
                {currencyList.map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Batch Type" value={editRow.batchType} onChange={(e) => handleEditRowChange('batchType', e.target.value)} fullWidth size="small">
                {batchTypes.map((bt) => <MenuItem key={bt.value} value={bt.value}>{bt.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Plan" value={editRow.subscriptionModel} onChange={(e) => handleEditRowChange('subscriptionModel', e.target.value)} fullWidth size="small">
                {subscriptionModels.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Classes/Week"
                type="number"
                value={editRow.classesPerWeek}
                onChange={(e) => handleEditRowChange('classesPerWeek', e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 7, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Price/Session" type="number" value={editRow.pricePerSession} onChange={(e) => handleEditRowChange('pricePerSession', e.target.value)} fullWidth size="small" inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Effective From" type="date" value={editRow.effectiveFrom} onChange={(e) => handleEditRowChange('effectiveFrom', e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRowModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={updateMutation.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {tabValue === 1 && (
      <Box mt={0}>
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <Box sx={cardHeaderSx}>
            <LocalOffer sx={{ color: 'primary.main', fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="700">Discount Catalog</Typography>
              <Typography variant="body2" color="text.secondary">Default value and lock type for each discount type.</Typography>
            </Box>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Default as %</TableCell>
                  <TableCell>Default Value</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Lock type</span>
                      <Tooltip
                        title={
                          "When locked, the discount’s %/flat behavior and default value can be enforced by policy. Finance users won’t be able to change it per subscription."
                        }
                        arrow
                      >
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <InfoOutlined fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discountCatalogQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>Loading...</TableCell>
                  </TableRow>
                ) : discountCatalog.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>No discount types.</TableCell>
                  </TableRow>
                ) : (
                  discountCatalog.map((d) => {
                    const e = getEdit(d);
                    return (
                      <TableRow key={d.type}>
                        <TableCell>{d.type}</TableCell>
                        <TableCell sx={{ minWidth: 240 }}>
                          <TextField
                            value={e.label}
                            onChange={(ev) =>
                              setEditField(d.type, 'label', ev.target.value)
                            }
                            size="small"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={!!e.defaultIsPercentage}
                            onChange={(ev) =>
                              setEditField(
                                d.type,
                                'defaultIsPercentage',
                                ev.target.checked
                              )
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <TextField
                            type="number"
                            value={e.defaultValue ?? ''}
                            onChange={(ev) =>
                              setEditField(
                                d.type,
                                'defaultValue',
                                ev.target.value === '' ? null : Number(ev.target.value)
                              )
                            }
                            size="small"
                            fullWidth
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={!!e.isValueLocked}
                            onChange={(ev) =>
                              setEditField(
                                d.type,
                                'isValueLocked',
                                ev.target.checked
                              )
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={!!e.isActive}
                            onChange={(ev) =>
                              setEditField(d.type, 'isActive', ev.target.checked)
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSaveDiscountType(d.type)}
                            disabled={updateDiscountCatalogMutation.isLoading}
                          >
                            Save
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      )}

      {tabValue === 2 && (
      <Box mt={0}>
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <Box sx={cardHeaderSx}>
            <AddCircleOutline sx={{ color: 'primary.main', fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="700">Add-on Catalog</Typography>
              <Typography variant="body2" color="text.secondary">Add-on types that can be attached to subscriptions.</Typography>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Label</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addOnCatalogQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={2}>Loading...</TableCell>
                  </TableRow>
                ) : addOnCatalog.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>No add-ons.</TableCell>
                  </TableRow>
                ) : (
                  addOnCatalog.map((a) => (
                    <TableRow key={a.key}>
                      <TableCell>{a.key}</TableCell>
                      <TableCell>{a.label}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 3, mt: 3, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="subtitle1" fontWeight="700" gutterBottom>
            Add-on pricing (country & currency)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Unit price per add-on type by country and currency.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                size="small"
                fullWidth
                label="Filter country"
                value={addOnPricingFilters.country}
                onChange={(e) => handleAddOnPricingFilterChange('country', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {countryList.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                size="small"
                fullWidth
                label="Filter currency"
                value={addOnPricingFilters.currency}
                onChange={(e) => handleAddOnPricingFilterChange('currency', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {currencyList.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                size="small"
                fullWidth
                label="Country"
                value={newAddOnRow.country}
                onChange={(e) => setNewAddOnRow((p) => ({ ...p, country: e.target.value, currency: countryToCurrency[e.target.value] || p.currency }))}
              >
                <MenuItem value="">Select</MenuItem>
                {countryList.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                size="small"
                fullWidth
                label="Currency"
                value={newAddOnRow.currency}
                onChange={(e) => setNewAddOnRow((p) => ({ ...p, currency: e.target.value }))}
              >
                <MenuItem value="">Select</MenuItem>
                {currencyList.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                size="small"
                fullWidth
                label="Add-on type"
                value={newAddOnRow.addOnType}
                onChange={(e) => setNewAddOnRow((p) => ({ ...p, addOnType: e.target.value }))}
              >
                <MenuItem value="">Select</MenuItem>
                {addOnCatalog.map((a) => (
                  <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                size="small"
                fullWidth
                label="Unit price"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={newAddOnRow.unitPrice}
                onChange={(e) => setNewAddOnRow((p) => ({ ...p, unitPrice: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleCreateAddOnPricing}
                disabled={createAddOnPricingMutation.isLoading}
              >
                Add
              </Button>
            </Grid>
          </Grid>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableRow-root td': { py: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Country</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Add-on type</TableCell>
                  <TableCell align="right">Unit price</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addOnPricingQuery.isLoading ? (
                  <TableRow><TableCell colSpan={5}>Loading...</TableCell></TableRow>
                ) : addOnPricingRows.length === 0 ? (
                  <TableRow><TableCell colSpan={5}>No add-on pricing. Add rows above.</TableCell></TableRow>
                ) : (
                  addOnPricingRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.country}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell>{addOnTypeToLabel(row.addOnType)}</TableCell>
                      <TableCell align="right">{row.unitPrice != null ? Number(row.unitPrice).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => handleOpenEditAddOn(row)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => deleteAddOnPricingMutation.mutate(row.id)}
                          disabled={deleteAddOnPricingMutation.isLoading}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={addOnPricingPagination.total || 0}
            page={(addOnPricingPagination.page || 1) - 1}
            onPageChange={(_, newPage) => handleAddOnPricingFilterChange('page', newPage + 1)}
            rowsPerPage={addOnPricingFilters.limit}
            onRowsPerPageChange={(e) => handleAddOnPricingFilterChange('limit', parseInt(e.target.value, 10) || 20)}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>

        <Dialog open={!!editAddOnId} onClose={() => setEditAddOnId(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit add-on unit price</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Unit price"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={editAddOnUnitPrice}
              onChange={(e) => setEditAddOnUnitPrice(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditAddOnId(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEditAddOn} disabled={updateAddOnPricingMutation.isLoading}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      )}
    </Box>
  );
};

export default FinancePricing;

